import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonModal,
} from '@ionic/react';
import { useAuth } from '@lidea/shared/data-access/core';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  clearOfflineCache,
  Offline,
  useSetupCacheControls,
} from '@lidea/shared/feature-offline';
import { QueryLoadingBar } from '@lidea/shared/ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { logOutOutline } from 'ionicons/icons';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LangSelect } from './../lang-select/lang-select';
import styles from './mobile-header.module.css';
import { OnlineStatusButton } from './online-status-button';

/* eslint-disable-next-line */
export interface MobileHeaderProps {
  headerExtension?: JSX.Element;
  defaultBackHref?: string;
}

export function MobileHeader(props: MobileHeaderProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const toolbarRef = useRef<HTMLIonToolbarElement>(null);
  const [presentAlert] = useIonAlert();
  const syncControls = useSetupCacheControls();

  const [presentOfflineModal, dismissOfflineModal] = useIonModal(Offline, {
    dismiss: () => dismissOfflineModal(),
    ...syncControls,
  });

  const handleNetworkClick = () => {
    presentOfflineModal({
      cssClass: 'page-modal',
    });
  };

  const handleLogout = () => {
    presentAlert({
      header: t('common:logout_alert_header'),
      message: t('common:logout_alert_message'),
      buttons: [
        {
          text: t('common:cancel'),
          role: 'cancel',
        },
        {
          text: t('common:confirm'),
          role: 'confirm',
          handler: async () => {
            await clearOfflineCache({ queryClient, reason: 'logout' });
            logout();
          },
        },
      ],
    });
  };

  return (
    <IonHeader>
      <IonToolbar
        ref={toolbarRef}
        style={{ overflow: 'visible', contain: 'none' }}
      >
        <IonButtons slot="start">
          <IonBackButton defaultHref={props.defaultBackHref}></IonBackButton>
        </IonButtons>
        <IonTitle>
          <div className={styles.logo}>
            <img
              alt="Lidea logo"
              src="/logo-250x150.jpg"
              style={{ height: 40 }}
            />
          </div>
        </IonTitle>
        <IonButtons slot="end">
          <LangSelect
            ionicButtonClassName={styles.toolbarAction}
            variant="ionic"
          />
          <OnlineStatusButton
            className={styles.toolbarAction}
            onClick={handleNetworkClick}
          />
          <IonButton
            className={styles.toolbarAction}
            fill="clear"
            onClick={handleLogout}
          >
            <IonIcon icon={logOutOutline} slot="icon-only"></IonIcon>
          </IonButton>
        </IonButtons>
        <QueryLoadingBar />
      </IonToolbar>
      {props.headerExtension}
    </IonHeader>
  );
}

export default MobileHeader;
